import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import axios from 'axios';
import { SOCKET_DEBUG_ENABLED, SOCKET_DEBUG_LOG_LIMIT } from './socketDebug';

/**
 * Owns the application's single websocket connection.
 *
 * The connection lives here rather than in Vuex on purpose. Vuex 3 keeps state
 * inside a Vue instance's `data`, so an Echo instance placed there gets deeply
 * observed — Vue installs reactive getters and setters across the whole
 * pusher-js object graph, on a hot path. Storing it also leaves nowhere to hang
 * "on every reconnect, do X", which is how the stale-socket-id and
 * never-resynced bugs got in.
 *
 * Only derived, serialisable facts (connection status, the presence roster)
 * belong in the store; `src/store/plugins/socket.js` mirrors them across.
 */

/**
 * Sent on every request so the API's `->toOthers()` can exclude us from our own
 * broadcasts. Pusher issues a **new** socket id on every reconnect, so this is
 * re-applied on each `connected` — a stale value silently breaks `->toOthers()`
 * and the acting user starts receiving their own events.
 */
const SOCKET_ID_HEADER = 'X-Socket-ID';

/**
 * Which states pusher cannot get itself out of, and so are ours to act on.
 *
 * Deliberately **not** `unavailable`. Pusher's documentation is explicit that
 * it "will automatically retry the connection every 15 seconds" in that state,
 * and the default strategy backs that up: `{loop: true, timeout: 15000,
 * timeoutLimit: 60000}`. Those retries run inside the strategy, below the
 * connection manager, so they never call `updateState` — the reported state
 * sits silently at `unavailable` while attempts continue underneath. Silence
 * there is health, not a stall.
 *
 * Acting on it was actively harmful: every nudge calls `disconnectInternally()`
 * → `abortConnecting()`, which throws away the strategy's escalating timeout
 * and restarts it at 15s. We were the reason pusher's backoff never reached
 * its 60s limit.
 *
 * Nor `failed`. Pusher documents that state as "Channels is not supported by
 * the browser... WebSockets are not natively available", and we take it at its
 * word: a replacement instance gets the same unsupported strategy and fails
 * identically, so rebuilding is futile — and unreleasable, since pusher-js
 * pushes every instance onto `Pusher.instances` and binds the module-global
 * `Network` emitter in the connection manager's constructor, unbinding from
 * neither. `failed` therefore ends realtime for this page; the status stays
 * `failed`, `hasChannelConnection` is false, and the app carries on over HTTP
 * until the user reloads.
 *
 * `disconnected` reaching this handler means pusher's `refused` callback ran
 * (close codes 4000–4099: app disabled, invalid key, and the transient
 * over-quota 4004). That path calls `disconnect()` and schedules nothing — no
 * retry timer, no unavailable timer, no strategy runner. Our own teardowns do
 * not appear here: they bump the generation first.
 */
const RECOVERABLE_STATES = ['disconnected'];

const RECOVERY_BASE_DELAY = 2000;
const RECOVERY_MAX_DELAY = 30000;

/** Roster with `member` appended, unless an entry with that id is already in. */
const withMember = (members, member) =>
    members.some(item => item.id === member.id) ? members : members.concat([member]);

/** Roster without `member`, matched on id. */
const withoutMember = (members, member) => members.filter(item => item.id !== member.id);

/**
 * Name an event for the debug log.
 *
 * Every whisper on a channel arrives under one binding, because the wire event
 * name is the channel name rather than the kind of change — so the log would
 * otherwise be a wall of identical `whisper` rows. The discriminator this app
 * actually uses is the payload's `type` (`lock`, `entries-saved`,
 * `diary-published`, …), so that is what gets shown.
 */
const logLabel = (key, payload) =>
    key === 'whisper' && payload && payload.type ? `whisper:${payload.type}` : key;

/**
 * Channel authorisation rides on the session cookie, which pusher-js's own XHR
 * does not send by default. `Runtime` is a module singleton, so overriding it
 * once here applies to every request the library makes.
 */
Pusher.Runtime.createXHR = function () {
    const xhr = new XMLHttpRequest();

    xhr.withCredentials = true;

    return xhr;
};

/**
 * Built lazily rather than at import time so the environment is read when the
 * connection is actually opened.
 */
const buildOptions = () => {
    const options = {
        broadcaster: 'pusher',
        // Injected explicitly — laravel-echo otherwise looks for a global
        // `Pusher`, which would mean leaking it onto `window`.
        Pusher,
        key: process.env.VUE_APP_PUSHER_APP_KEY,
        wsHost: process.env.VUE_APP_PUSHER_APP_HOSTNAME,
        // Set during local development; deployed environments fall through to
        // pusher-js's default SSL port.
        wsPort: process.env.VUE_APP_PUSHER_APP_PORT,
        disableStats: true,
        forceTLS: false,
        encrypted: true,
        enabledTransports: ['ws', 'wss'],
        auth: {
            headers: {
                Accept: 'application/json'
            }
        }
    };

    if (process.env.VUE_APP_API_URL) {
        options.authEndpoint = process.env.VUE_APP_API_URL + '/broadcasting/auth';
    }

    return options;
};

class SocketService {
    constructor() {
        if (SocketService.instance) {
            return SocketService.instance;
        }

        this._echo = null;
        this._status = '';
        this._statusListeners = [];
        this._reconnectListeners = [];
        this._hasConnected = false;
        this._channels = {};
        this._subscriptionListeners = [];
        this._subscriptionSignature = '';
        this._primaryByOwner = new Map();
        this._reconnectCount = 0;
        this._lastStateChangeAt = null;
        this._log = [];
        this._recoveryTimer = null;
        this._generation = 0;
        this._recoveryAttempts = 0;
        this._onOnline = () => {
            // The listener spans the whole connection, so it fires on healthy
            // ones too — and acting on those would tear down a working socket.
            if (this.isConnected()) {
                return;
            }

            // Cancel the pending attempt rather than racing it: without this,
            // the timer fires moments later and we recover twice for one
            // event. The attempt counter restarts because a network change is
            // genuinely new information.
            this._clearRecoveryTimer();
            this._recoveryAttempts = 0;

            this._recoverNow();
            this._scheduleRecovery();
        };

        SocketService.instance = this;
    }

    /**
     * Open the connection. A no-op once connected, so callers entering a module
     * may call it freely.
     */
    connect() {
        if (this._echo) {
            return;
        }

        this._echo = new Echo(buildOptions());

        this._bindConnectionEvents();

        if (typeof window !== 'undefined') {
            window.addEventListener('online', this._onOnline);
        }
    }

    /**
     * Drop the connection. Safe to call when never connected.
     */
    disconnect() {
        this._clearRecovery();

        // Nothing the old connection says afterwards concerns us.
        this._generation += 1;

        if (typeof window !== 'undefined') {
            window.removeEventListener('online', this._onOnline);
        }

        if (this._echo) {
            Object.keys(this._channels).forEach(name => this._echo.leave(name));
            this._echo.disconnect();
            this._echo = null;
        }

        this._channels = {};
        this._primaryByOwner = new Map();

        this._notifySubscriptionChange();

        delete axios.defaults.headers.common[SOCKET_ID_HEADER];

        // An explicit teardown is a deliberate stop, so the next `connect()`
        // starts fresh: its first `connected` is not a reconnect, and callers
        // load their own data rather than being told to resync.
        this._hasConnected = false;

        this._setStatus('disconnected');
    }

    /**
     * Attach a set of handlers to a channel, joining it if nobody has yet.
     * Connects first if needed, so callers may call this freely.
     *
     * Idempotent per `(name, owner)`: calling it again for the same owner
     * **replaces** that owner's handler table rather than adding another
     * binding. Underneath, each event is bound to the channel exactly once for
     * the channel's lifetime and dispatched to every owner, so no amount of
     * re-subscribing can stack listeners — which is what made repeated toasts
     * and double-counted badges possible before.
     *
     * Returns nothing on purpose: a raw pusher channel must never escape this
     * service, or it ends up in a component's reactive `data()`.
     *
     * @param {string} name
     * @param {Object} handlers `{type, listen: {event: fn}, whisper, here, joining, leaving}`
     * @param {*} owner Any unique value; components pass `this`.
     * @param {Object} [options] `{primary}` — a primary subscription replaces
     *   this owner's previous one, which is how a view re-keys onto a new date
     *   or course without having to remember to leave the old channel.
     */
    subscribe(name, handlers, owner, options = {}) {
        this.connect();

        if (options.primary) {
            const previous = this._primaryByOwner.get(owner);

            if (previous && previous !== name) {
                this.unsubscribe(previous, owner);
            }

            this._primaryByOwner.set(owner, name);
        }

        let record = this._channels[name];

        if (!record) {
            const type = handlers.type === 'private' ? 'private' : 'presence';

            record = {
                name,
                type,
                // Whisper-only channels still join as presence: switching them
                // to private would change the wire prefix and break both the
                // API's channel routes and the mobile client.
                channel: type === 'private' ? this._echo.private(name) : this._echo.join(name),
                owners: new Map(),
                boundEvents: new Set(),
                // Null until the roster arrives, so an incomplete list is never
                // replayed to a late owner.
                members: null,
                subscribed: false
            };

            this._channels[name] = record;

            this._bindSubscriptionState(record);

            if (type === 'presence') {
                this._bindPresence(record);
            }
        }

        record.owners.set(owner, handlers);

        this._bindHandlers(record, handlers);

        // `here` only fires on `pusher:subscription_succeeded`, so an owner
        // attaching to an already-subscribed channel would otherwise never
        // learn who is present. Hand it the cached roster instead.
        if (record.members && handlers.here) {
            handlers.here(record.members);
        }
    }

    /**
     * Detach one owner from a channel. The channel itself is left only once the
     * last owner has gone — `Map.vue` and `MassFillModal.vue` deliberately hold
     * the same channel, and either leaving it outright would kill the other's
     * subscription.
     */
    unsubscribe(name, owner) {
        if (this._primaryByOwner.get(owner) === name) {
            this._primaryByOwner.delete(owner);
        }

        const record = this._channels[name];

        if (!record) {
            return;
        }

        record.owners.delete(owner);

        if (record.owners.size) {
            return;
        }

        delete this._channels[name];

        if (this._echo) {
            this._echo.leave(name);
        }

        this._notifySubscriptionChange();
    }

    /**
     * Detach an owner from every channel it holds. One call in a component's
     * teardown, rather than each view remembering which channels it opened —
     * forgetting that is what leaks channels today.
     */
    unsubscribeAll(owner) {
        // Snapshot the names: `unsubscribe` deletes from the object it iterates.
        Object.keys(this._channels).forEach(name => {
            if (this._channels[name].owners.has(owner)) {
                this.unsubscribe(name, owner);
            }
        });
    }

    /**
     * Send a client event on a channel. The wire event name equals the channel
     * name; Echo adds the `client-` prefix.
     *
     * Gated on the channel actually being subscribed, not merely on the socket
     * being up — pusher-js only logs a warning for a client event on an
     * unsubscribed channel and then silently drops it. Returns whether it went.
     */
    whisper(name, payload) {
        const record = this._channels[name];

        if (!record || !this.isSubscribed(name)) {
            return false;
        }

        try {
            record.channel.whisper(name, payload);

            this._record(name, 'whisper', 'out', payload);

            return true;
        } catch (error) {
            // Joined but not yet subscribed: pusher-js throws on the missing
            // subscription. Dropping the whisper is the right outcome.
            return false;
        }
    }

    /**
     * Whether this channel is actually carrying events.
     *
     * Distinct from `isConnected()` on purpose. The transport can be up while a
     * channel is dead — an expired session makes `/broadcasting/auth` fail on
     * resubscribe, and the old code had no way to tell, so views happily
     * whispered into a void believing they were connected.
     */
    isSubscribed(name) {
        const record = this._channels[name];

        return Boolean(record && record.subscribed);
    }

    /**
     * Subscribe to changes in the set of live channels. Fires immediately with
     * the current names, then whenever a channel subscribes, fails, is left, or
     * is invalidated by the connection dropping. Returns the unsubscribe
     * function.
     */
    onSubscriptionChange(listener) {
        this._subscriptionListeners.push(listener);

        listener(this._subscribedNames());

        return () => {
            this._subscriptionListeners = this._subscriptionListeners.filter(item => item !== listener);
        };
    }

    /**
     * Subscribe to connection status changes. Fires immediately with the
     * current status, so a caller never has to handle "not known yet"
     * separately. Returns the unsubscribe function.
     */
    onStatus(listener) {
        this._statusListeners.push(listener);

        listener(this._status);

        return () => {
            this._statusListeners = this._statusListeners.filter(item => item !== listener);
        };
    }

    /**
     * Subscribe to reconnections — every `connected` *after* the first one.
     *
     * Deliberately silent on the first connect: callers load their own data
     * when they mount. Every later connect means events were missed while the
     * socket was down, and those are unrecoverable, so the only safe response
     * is to refetch. Returns the unsubscribe function.
     */
    onReconnect(listener) {
        this._reconnectListeners.push(listener);

        return () => {
            this._reconnectListeners = this._reconnectListeners.filter(item => item !== listener);
        };
    }

    /**
     * One of pusher's connection states: 'initialized', 'connecting',
     * 'connected', 'unavailable', 'failed' or 'disconnected'. Empty before the
     * first connection attempt.
     */
    getStatus() {
        return this._status;
    }

    isConnected() {
        return this._status === 'connected';
    }

    getSocketId() {
        if (!this._echo) {
            return null;
        }

        return this._echo.socketId() || null;
    }

    /**
     * Plain, serialisable picture of the connection for the dev/test overlay.
     *
     * Deliberately holds no live pusher reference — a raw channel escaping here
     * would land in the overlay's reactive data and reintroduce the deep
     * observation this refactor removes. Counts and names only.
     *
     * The event log is empty unless `SOCKET_DEBUG_ENABLED`.
     */
    getDebugSnapshot() {
        return {
            status: this._status,
            socketId: this.getSocketId(),
            reconnectCount: this._reconnectCount,
            lastStateChangeAt: this._lastStateChangeAt,
            channels: Object.keys(this._channels).map(name => {
                const record = this._channels[name];

                return {
                    name,
                    type: record.type,
                    subscribed: record.subscribed,
                    owners: record.owners.size,
                    boundEvents: Array.from(record.boundEvents),
                    members: record.members ? record.members.length : 0
                };
            }),
            unmanagedChannels: this._unmanagedChannelNames(),
            events: this._log.slice()
        };
    }

    /**
     * Channels that exist on the connection but were not opened through
     * `subscribe()`.
     *
     * Should always be empty: everything goes through `subscribe()` now. A name
     * appearing here means something reached for the raw Echo instance, which
     * the service cannot refcount, rebuild or report on — so the overlay
     * showing a row is a bug worth chasing.
     */
    _unmanagedChannelNames() {
        const connector = this._echo && this._echo.connector;

        if (!connector || !connector.channels) {
            return [];
        }

        return Object.keys(connector.channels)
            .map(key => key.replace(/^(presence-|private-encrypted-|private-)/, ''))
            .filter(name => !this._channels[name]);
    }

    /**
     * Bound exactly once, from inside `connect()`'s guard, so repeated
     * `connect()` calls cannot stack handlers.
     */
    _bindConnectionEvents() {
        const connection = this._echo.connector.pusher.connection;

        // Stamped so events from a connection we have already walked away from
        // can be told apart from the live one's. There is no unbind here —
        // laravel-echo gives no handle on the connection it wraps — so the
        // check below is what actually disowns it.
        const generation = ++this._generation;

        // `new Echo()` connects synchronously when the document is already
        // ready — the `Pusher` constructor ends with `this.connect()` — so the
        // first `connecting` fires before this binding exists. Without seeding,
        // the service reports '' until the next transition, which for an
        // unreachable server is ten seconds away.
        if (connection.state) {
            this._setStatus(connection.state);
        }

        // A single binding covers all six states; pusher reports every
        // transition through `state_change`.
        connection.bind('state_change', ({ current }) => {
            // `disconnect()` abandons a connection without stopping its
            // timers, and pusher's `setUnavailableTimer` overwrites the
            // previous timer rather than aborting it — so a connection we have
            // walked away from can still report `unavailable` up to
            // `unavailableTimeout` (10s) later. That is not news about the
            // connection we actually have.
            if (generation !== this._generation) {
                return;
            }

            // Applied before listeners run, so anything a listener triggers —
            // a reconnect resync refetch in particular — already carries the
            // fresh socket id.
            if (current === 'connected') {
                this._applySocketId();
            }

            // Recorded before the triggers below, so they read the state the
            // connection is actually in. Scheduling first left `_status` one
            // transition behind, which made the rebuild-budget check consult a
            // value that could never match.
            //
            // Gated on an actual transition: a repeated `connected` must not
            // be mistaken for a reconnect and trigger a spurious resync.
            const changed = this._setStatus(current);

            // Only a connection that actually came back clears the backoff.
            // Clearing on any other state meant pusher's own retry loop reset
            // it: `retryIn` emits `connecting` asynchronously, so every
            // internal retry zeroed `_recoveryAttempts` and the delay never
            // grew past its opening 2s. An explicit `disconnect()` does not
            // come through here at all — it clears recovery itself.
            if (current === 'connected') {
                this._clearRecovery();
            } else if (RECOVERABLE_STATES.includes(current)) {
                this._scheduleRecovery();
            }

            if (!changed || current !== 'connected') {
                return;
            }

            if (this._hasConnected) {
                this._reconnectCount += 1;

                this._reconnectListeners.slice().forEach(listener => listener());
            }

            this._hasConnected = true;
        });
    }

    /**
     * Bind whatever this handler table needs that is not bound yet. Every
     * binding goes through a trampoline that reads `record.owners` at call
     * time, so replacing an owner's handlers takes effect with no rebinding.
     */
    _bindHandlers(record, handlers) {
        Object.keys(handlers.listen || {}).forEach(event => {
            this._bindOnce(record, `listen:${event}`, dispatch => {
                record.channel.listen(event, payload => {
                    dispatch(owner => owner.listen && owner.listen[event], payload);
                });
            });
        });

        if (handlers.whisper) {
            this._bindOnce(record, 'whisper', dispatch => {
                // The wire event name equals the channel name; Echo adds the
                // `client-` prefix. This matches the mobile app's protocol.
                record.channel.listenForWhisper(record.name, payload => {
                    dispatch(owner => owner.whisper, payload);
                });
            });
        }

    }

    /**
     * Bound for every channel regardless of what owners asked for: the service
     * has to know whether a channel is live, and `pusher:subscription_error` is
     * the only signal that authorisation failed.
     */
    _bindSubscriptionState(record) {
        this._bindOnce(record, 'subscribed', dispatch => {
            record.channel.subscribed(() => {
                record.subscribed = true;

                this._notifySubscriptionChange();
                dispatch(owner => owner.subscribed, undefined);
            });
        });

        this._bindOnce(record, 'error', dispatch => {
            record.channel.error(status => {
                record.subscribed = false;

                this._notifySubscriptionChange();
                dispatch(owner => owner.error, status);
            });
        });
    }

    /**
     * Append to the rolling event log. A no-op in staging and production, where
     * the flag folds to `false` and the minifier drops the branch.
     */
    _record(channel, key, direction, payload) {
        if (!SOCKET_DEBUG_ENABLED) {
            return;
        }

        // Named inside the guard, so staging and production pay nothing beyond
        // the branch the minifier folds away.
        this._log.push({ at: Date.now(), channel, event: logLabel(key, payload), direction });

        if (this._log.length > SOCKET_DEBUG_LOG_LIMIT) {
            this._log.shift();
        }
    }

    _subscribedNames() {
        return Object.keys(this._channels).filter(name => this._channels[name].subscribed);
    }

    _notifySubscriptionChange() {
        const names = this._subscribedNames();
        const signature = names.join('|');

        // Only on a real change, so the store is not committed to on every
        // event that happens to touch a channel.
        if (signature === this._subscriptionSignature) {
            return;
        }

        this._subscriptionSignature = signature;

        this._subscriptionListeners.slice().forEach(listener => listener(names));
    }

    /**
     * Presence callbacks are bound when the channel is created, not when some
     * owner asks for them. The roster has to be tracked from the start: if the
     * first owner wants no presence and a later one does, `here` would already
     * have fired and there would be nothing to hand the newcomer.
     */
    _bindPresence(record) {
        this._bindOnce(record, 'here', dispatch => {
            record.channel.here(members => {
                record.members = members || [];

                dispatch(owner => owner.here, record.members);
            });
        });

        this._bindOnce(record, 'joining', dispatch => {
            record.channel.joining(member => {
                if (record.members) {
                    record.members = withMember(record.members, member);
                }

                dispatch(owner => owner.joining, member);
            });
        });

        this._bindOnce(record, 'leaving', dispatch => {
            record.channel.leaving(member => {
                if (record.members) {
                    record.members = withoutMember(record.members, member);
                }

                dispatch(owner => owner.leaving, member);
            });
        });
    }

    /**
     * Run `bind` unless this key is already bound on the channel, handing it a
     * `dispatch(pick, payload)` that fans the event out to every owner.
     */
    _bindOnce(record, key, bind) {
        if (record.boundEvents.has(key)) {
            return;
        }

        record.boundEvents.add(key);

        bind((pick, payload) => {
            this._record(record.name, key, 'in', payload);

            // A copy: an owner may unsubscribe from inside its own handler.
            Array.from(record.owners.values()).forEach(handlers => {
                const fn = pick(handlers);

                if (fn) {
                    fn(payload);
                }
            });
        });
    }

    /**
     * Nudge a stuck connection back to life, with backoff.
     *
     * `pusher.connect()` returns immediately when a connection or strategy
     * runner already exists, so this is harmless whenever pusher is still
     * retrying on its own — it only bites when it has genuinely given up.
     */
    _scheduleRecovery() {
        if (this._recoveryTimer) {
            return;
        }

        const delay = Math.min(
            RECOVERY_BASE_DELAY * Math.pow(2, this._recoveryAttempts),
            RECOVERY_MAX_DELAY
        );

        this._recoveryTimer = setTimeout(() => {
            this._recoveryTimer = null;
            this._recoveryAttempts += 1;

            this._recoverNow();

            // Keep trying: a tab left open should come back whenever the
            // server does, however long that takes.
            if (!this.isConnected()) {
                this._scheduleRecovery();
            }
        }, delay);
    }

    _clearRecoveryTimer() {
        if (this._recoveryTimer) {
            clearTimeout(this._recoveryTimer);
            this._recoveryTimer = null;
        }
    }

    _clearRecovery() {
        this._clearRecoveryTimer();

        this._recoveryAttempts = 0;
    }

    /**
     * Force a fresh connection attempt.
     *
     * `connect()` alone is not enough: it early-returns on
     * `if (this.connection || this.runner)`, and a stalled strategy runner is
     * exactly what is left behind in the stuck states — `abandonConnection()`
     * clears `connection` but only `abortConnecting()` clears `runner`, and
     * `startConnecting` keeps re-assigning it on each internal retry. So the
     * runner has to be torn down first, which is what `disconnect()` does.
     */
    _recoverNow() {
        if (!this._echo) {
            return;
        }

        // `refused` tore everything down and scheduled nothing, so there is no
        // in-flight attempt to preserve and `connect()` will start a fresh one.
        if (this._status === 'disconnected') {
            this._echo.connector.pusher.connect();
        }

        // Every other state is pusher's own: `unavailable` on the schedule its
        // documentation promises, `failed` because it is genuinely terminal.
        // Interrupting either only resets a schedule or leaks an instance.
    }

    /**
     * Unconditional by design: the previous implementation guarded with
     * `if (!socketId)` and so never refreshed the header after a reconnect.
     */
    _applySocketId() {
        const socketId = this.getSocketId();

        if (socketId) {
            axios.defaults.headers.common[SOCKET_ID_HEADER] = socketId;
        }
    }

    /** Returns whether the status actually changed. */
    _setStatus(status) {
        if (this._status === status) {
            return false;
        }

        this._status = status;
        this._lastStateChangeAt = Date.now();

        // A dropped transport invalidates every subscription. Without this a
        // channel would still read as subscribed while the socket was down,
        // and pusher re-subscribes on reconnect anyway.
        if (status !== 'connected') {
            Object.keys(this._channels).forEach(name => {
                this._channels[name].subscribed = false;
            });

            this._notifySubscriptionChange();
        }

        // Iterate a copy: a listener is allowed to unsubscribe itself from
        // inside its own callback, which would otherwise shift the array
        // mid-traversal and skip the next listener.
        this._statusListeners.slice().forEach(listener => listener(status));

        return true;
    }
}

export default new SocketService();
